import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import { buildBaseFields, buildUpdateFields, writeAuditLog } from '@shared/utils';
import type { RolePreset, RolesUser, PermissionAction, ID } from '@shared/types';

const PRESET_COL = 'presets';
const ROLES_USER_COL = 'user_roles';

const PERMISSION_ACTIONS = [
  'create',
  'read',
  'update',
  'delete',
  'export',
  'restore',
  'setDefault',
  'print',
] as const;

const presetSchema = z.object({
  name: z.string().min(1, 'プリセット名は必須です。'),
  description: z.string().default(''),
  permissions: z.record(
    z.array(z.enum(PERMISSION_ACTIONS))
  ),
  isDefault: z.boolean().default(false),
});

export type PresetCreateInput = z.infer<typeof presetSchema>;

export async function fetchPresets(): Promise<RolePreset[]> {
  try {
    const q = query(
      collection(db, PRESET_COL),
      where('isDeleted', '==', false),
      orderBy('name')
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as RolePreset);
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`プリセット一覧の取得に失敗しました: ${msg}`);
  }
}

export async function fetchPreset(id: ID): Promise<RolePreset | null> {
  try {
    const snap = await getDoc(doc(db, PRESET_COL, id));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() } as RolePreset;
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`プリセットの取得に失敗しました: ${msg}`);
  }
}

export async function createPreset(
  data: PresetCreateInput,
  userId: ID
): Promise<RolePreset> {
  try {
    const validated = parseOrThrow(presetSchema, data);
    const payload = { ...validated, ...buildBaseFields(userId) };
    const ref = await addDoc(collection(db, PRESET_COL), payload);
    await writeAuditLog({
      entityType: 'preset',
      entityId: ref.id,
      action: 'create',
      userId,
      after: validated,
    });
    return { id: ref.id, ...payload } as unknown as RolePreset;
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`プリセットの作成に失敗しました: ${msg}`);
  }
}

export async function updatePreset(
  id: ID,
  data: Partial<PresetCreateInput>,
  userId: ID
): Promise<RolePreset> {
  try {
    const ref = doc(db, PRESET_COL, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('プリセットが見つかりません。');
    const patch = { ...data, ...buildUpdateFields(userId) };
    await updateDoc(ref, patch);
    await writeAuditLog({
      entityType: 'preset',
      entityId: id,
      action: 'update',
      userId,
      before: snap.data(),
      after: data,
    });
    return { id, ...snap.data(), ...patch } as unknown as RolePreset;
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`プリセットの更新に失敗しました: ${msg}`);
  }
}

export async function deletePreset(id: ID, userId: ID): Promise<void> {
  try {
    const ref = doc(db, PRESET_COL, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('プリセットが見つかりません。');
    await updateDoc(ref, { isDeleted: true, ...buildUpdateFields(userId) });
    await writeAuditLog({
      entityType: 'preset',
      entityId: id,
      action: 'delete',
      userId,
      before: snap.data(),
    });
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`プリセットの削除に失敗しました: ${msg}`);
  }
}

export async function setUserPreset(
  userId: ID,
  presetId: ID | null,
  overrides: Record<string, PermissionAction[]>,
  createdBy: ID
): Promise<RolesUser> {
  try {
    const q = query(
      collection(db, ROLES_USER_COL),
      where('userId', '==', userId),
      where('isDeleted', '==', false)
    );
    const snap = await getDocs(q);

    let effectivePermissions: Record<string, PermissionAction[]> = { ...overrides };
    if (presetId) {
      const presetSnap = await getDoc(doc(db, PRESET_COL, presetId));
      if (presetSnap.exists()) {
        const preset = presetSnap.data() as RolePreset;
        const merged: Record<string, PermissionAction[]> = { ...preset.permissions };
        Object.entries(overrides).forEach(([resource, actions]) => {
          merged[resource] = actions;
        });
        effectivePermissions = merged;
      }
    }

    const payload = {
      userId,
      presetId,
      overrides,
      effectivePermissions,
      ...buildBaseFields(createdBy),
    };

    if (snap.empty) {
      const ref = await addDoc(collection(db, ROLES_USER_COL), payload);
      await writeAuditLog({
        entityType: 'user_roles',
        entityId: ref.id,
        action: 'create',
        userId: createdBy,
        after: { userId, presetId },
      });
      return { id: ref.id, ...payload } as unknown as RolesUser;
    }

    const existing = snap.docs[0];
    const patch = {
      presetId,
      overrides,
      effectivePermissions,
      ...buildUpdateFields(createdBy),
    };
    await updateDoc(existing.ref, patch);
    await writeAuditLog({
      entityType: 'user_roles',
      entityId: existing.id,
      action: 'update',
      userId: createdBy,
      after: { userId, presetId },
    });
    return { id: existing.id, ...existing.data(), ...patch } as unknown as RolesUser;
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`ユーザー権限の設定に失敗しました: ${msg}`);
  }
}

export async function getUserRoles(userId: ID): Promise<RolesUser | null> {
  try {
    const q = query(
      collection(db, ROLES_USER_COL),
      where('userId', '==', userId),
      where('isDeleted', '==', false)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    return { id: snap.docs[0].id, ...snap.docs[0].data() } as unknown as RolesUser;
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    throw new Error(`ユーザー権限の取得に失敗しました: ${msg}`);
  }
}
